import React, { createContext, useContext, useState, useEffect } from "react";
import { auth } from "lib/firebase"
import { onAuthStateChanged, signOut, verifyPasswordResetCode, confirmPasswordReset, applyActionCode } from "firebase/auth";
import { detectCompanySwitch, selectMembership, selectMembershipId, clearSelection } from "lib/companySwitch";
import { paths } from "routes/paths";
import { clearReturnTo } from "lib/returnTo";
import { loginWithEmailAndPassword, getSession, endSession, getErrorCode, toApiError, verifyExistance, register as registerAPI, signUp, forgotPassword, requestAccess, createFirebaseUser, sendVerificationEmail } from "features/auth"

const AuthContext = createContext();

const SILENT_SESSION_ERRORS = ['account_not_found', 'email_not_verified'];

const currentUserOf = (account) => account?.users?.find((user) => user.user_id === account.current_user_id) ?? null;

const AuthProvider = ({ children }) => {  
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [user, setUser] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null)
  const [companySwitch, setCompanySwitch] = useState(null);

  const applySession = (accountData) => {
    setError(null);
    setFirebaseUser(auth.currentUser);
    setAccount(accountData);
    const current = currentUserOf(accountData);
    setUser(current);
    const switched = detectCompanySwitch(accountData, current);
    if (switched) {
      setCompanySwitch(switched);
    }
  }

  const dismissCompanySwitch = () => setCompanySwitch(null);

  useEffect(() => {
    const onAuthStateChange = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const session = await getSession();
        applySession(session.data);
      } catch (error) {
        // A login that has no account yet, or hasn't verified its email, is in
        // the middle of signing up; keep it signed in to Firebase without a session.
        if (!SILENT_SESSION_ERRORS.includes(getErrorCode(error))) {
          logout();
          setError(toApiError(error).message || "Failed to retrieve account data.");
        }
      }

      setLoading(false);
    });
  
    return () => {
     onAuthStateChange();
    }
  }, [])

  const login = async (data) => {
    try {
      const response = await loginWithEmailAndPassword(data);
      if (response?.success) {
        applySession(response.data);
      }

      return response;
    } catch (error) {
      await logout();
      throw error;
    }
  }

  const refreshSession = async (userID) => {
    if (userID) {
      selectMembershipId(userID);
      setCompanySwitch(null);
    }

    const session = await getSession();
    applySession(session.data);
    return session.data;
  }

  const switchCompany = (membership) => {
    selectMembership(account, membership);
    window.location.assign(paths.homepage);
  }

  const logout = async () => {
    await endSession();
    await signOut(auth);
    clearSelection();
    clearReturnTo();
    setFirebaseUser(null);
    setAccount(null);
    setUser(null);
    setCompanySwitch(null);
  }

  const checkExistance = async (email, vat) => {
    const result = await verifyExistance(email, vat);
    return result?.data;
  }

  const register = async (data, newFirebaseUser = true) => {
    try {
      return await registerAPI(data, newFirebaseUser);
    } catch (error) {
      if (newFirebaseUser) {
        await logout();
      }
      throw error;
    }
  }

  const createfirebaseuser = async (data) => {
    try {
      return await createFirebaseUser(data);
    } catch (error) {
      await logout();
      throw error;
    }
  }

  const forgotpassword = async (email) => {
    try {
      await forgotPassword(email);
    } catch (error) {
      throw error;
    }
  }

  const resetPassword = async (actionCode, password) => {
    try {
      await confirmPasswordReset(auth, actionCode, password);
    } catch (error) {
      throw error;
    }
  }

  const verifyResetCode = async (actionCode) => {
    try {
      return await verifyPasswordResetCode(auth, actionCode);
    } catch (error) {
      if (error.code === 'auth/expired-action-code') {
        throw new Error("Code expired. Please try again.");
      } else if (error.code === 'auth/invalid-action-code') {
        throw new Error("Invalid code. Please try again.")
      } else {
        throw error;
      }
    }
  }

  const verifyActionCode = async (actionCode) => {
    try {
      return await applyActionCode(auth, actionCode);
    } catch (error) {
      if (error.code === 'auth/expired-action-code') {
        throw new Error("Code expired. Please try again.");
      } else if (error.code === 'auth/invalid-action-code') {
        throw new Error("Invalid code. Please try again.")
      } else {
        throw error;
      }
    }
  }

  const requestaccess = async(data, message) => {
    try {
      return await requestAccess(data, message)      
    } catch (error) {
      throw error
    }
  }

  const signup = async(data) => {
    try {
      return await signUp(data);
    } catch (error) {
      throw error
    }
  }

  const sendverificationemail = async () => {
    return await sendVerificationEmail();
  }

  return (
    <AuthContext.Provider value={{ firebaseUser, account, user, login, logout, refreshSession, checkExistance, register, signup, forgotpassword, resetPassword, verifyResetCode, verifyActionCode, requestaccess, createfirebaseuser, sendverificationemail, companySwitch, dismissCompanySwitch, switchCompany, error }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('context error')
  }

  return context
}

export { AuthProvider, useAuth };